import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Button,
  Checkbox,
  ConfigProvider,
  List,
  ListItem,
  Modal,
  notification,
  SearchBox,
  StatusTip,
  StatusTipProps,
  TagOption
} from 'tea-component';
import {debounce} from 'lodash-es';
import {useDblIntl} from '../../utils/utils';
import {useRequest} from "ahooks";
import {
  saveSharedEditors,
  searchUsers,
  type TSearchUserListData,
} from '@dbl/services/global';
import './index.scss';

export interface AccessSettingModalProps {
  visible: boolean; // 控制Modal的显示与隐藏
  onClose: (isSuccess?: boolean) => void; // Modal关闭事件的回调函数
  id: number; // 报表或者图表id
  creatorId: number | string; // 创建人id
  editors: Editor[]; // 有编辑权限的人员选项
  type: 'dashboard' | 'chart' | 'cube' | 'sql_view'; // 报表或者图表的权限弹窗
  className?: ''; // 类名
  style?: React.CSSProperties; // 内联样式
}

export interface Editor {
  userName: string;
  userId: string;
  isView: boolean;
  isDevelop: boolean;
}

const AccessSettingModal: React.FC<AccessSettingModalProps> = ({
  visible,
  onClose,
  id,
  creatorId,
  editors = [],
  type,
  className = '',
  style
}) => {
  
  const {dblFormatMessage, commonFormatMessage, isEn} = useDblIntl('dashboard_access');
  
  const [editorList, setEditorList] = useState<Editor[]>([]);
  
  useEffect(() => {
    setEditorList(editors);
  }, [editors]);
  
  
  // 确认权限接口
  const {loading: accessLoading, run: runSaveSharedEditors} = useRequest(saveSharedEditors, {
    manual: true,
    onError: (error) => {
      notification.error({
        // @ts-ignore
        description: dblFormatMessage("error", {code: error?.code || ''}),
      });
    },
    onSuccess: () => {
      notification.success({
        description: dblFormatMessage("success"),
      });
      onClose?.(true);
    }
  });
  
  
  return (
    <Modal
      visible={visible}
      caption={dblFormatMessage(`title_${type}`)}
      onClose={() => {
        !accessLoading && onClose();
      }}
      className={`dbl-access-setting-modal ${className}`}
      style={style}
    >
      <div className="dbl-access-setting-modal-body">
        {type === 'dashboard' ? (<div className="info-text">{dblFormatMessage('info')}</div>) : ''}
        <AddUser
          formatMessage={dblFormatMessage}
          creatorId={creatorId}
          onSelectUser={({text, value}: { text: string; value: string; }) => {
            const newEditor = {userId: value, userName: text, isView: false, isDevelop: false};
            setEditorList([...editorList, newEditor]);
          }}
          editorIds={editorList.map(item => item.userId)}
          isEn={isEn}
        />
        
        <div className={'editor-table'}>
          
          <div className={'editor-table-row title'}>
            <div className={'editor-table-user'}>{dblFormatMessage('user')}</div>
            <div className={'editor-table-access'}>{dblFormatMessage('access')}</div>
          </div>
          
          <div className={'editor-table-body'}>
            {
              editorList?.map((editor: Editor) => {
                try {
                  const {userName, userId, isView, isDevelop} = editor;
                  
                  return (
                    <div className={'editor-table-row'}>
                      <div className={'editor-table-user'}>{userName}</div>
                      <div className={'editor-table-access'}>
                        {type !== 'sql_view' && (
                          <Checkbox
                          // disabled={type === 'chart'}
                            value={isView}
                            onChange={(value) => {
                              setEditorList((editorList) => {
                                const editor = editorList.find((item) => item.userId === userId);
                                editor && (editor.isView = value);
                                return [...editorList];
                              });
                            }}
                          >
                            {dblFormatMessage('viewer')}
                          </Checkbox>
                        )}
                        
                        <Checkbox
                          value={isDevelop}
                          onChange={(value) => {
                            setEditorList((editorList) => {
                              const editor = editorList.find((item) => item.userId === userId);
                              editor && (editor.isDevelop = value);
                              return [...editorList];
                            });
                          }}
                        >
                          {type === 'sql_view' ? dblFormatMessage('sql_view_developer') : dblFormatMessage('developer')}
                        </Checkbox>
                      
                      </div>
                    </div>
                  );
                } catch (e) {
                  return '';
                }
                
              })
            }
          </div>
        
        </div>
      
      </div>
      <div className="dbl-access-setting-modal-footer">
        <Button
          type="primary"
          loading={accessLoading}
          onClick={() => {
            const permission: any[] = [];
            editorList.forEach(editor => {
              const access: string[] = [];
              editor.isView && access.push('read');
              editor.isDevelop && access.push('read', 'delete', 'edit');
              type === 'dashboard' && editor.isDevelop && access.push('publish');
              type === 'cube' && editor.isDevelop && access.push('publish');
              // 后端说sql 视图不传publish
              // type === 'sql_view' && editor.isDevelop && access.push('publish');
              permission.push({
                user_id: editor.userId,
                permission: access
              });
            });
            runSaveSharedEditors({
              // eslint-disable-next-line no-nested-ternary
              type: type === 'sql_view' ? 3 : type === 'cube' ? 2 : type === 'dashboard' ? 0 : 1,
              id,
              permission,
            });
          }}
        >
          {commonFormatMessage('confirm')}
        </Button>
        <Button
          onClick={() => onClose?.()}
          disabled={accessLoading}
        >
          {commonFormatMessage('cancel')}
        </Button>
      </div>
    </Modal>
  );
};


function AddUser(props: any) {
  const { formatMessage, creatorId, onSelectUser, editorIds = [], isEn } = props;

  const [options, setOptions] = useState<TagOption[]>([]);
  const [status, setStatus] = useState<StatusTipProps["status"] | null>(null);
  const searchStrRef = useRef<string>('');

  // @ts-ignore
  const { run: getUserList, cancel: cancelUserList } = useRequest(searchUsers, {
    manual: true,
    onError: () => {
      setStatus('error');
    },
    onSuccess: (data: TSearchUserListData) => {
      if (searchStrRef.current) {
        const options: TagOption[] = [];
        // 搜索添加时, 过滤要将已添加的用户和创建者去掉, 用户id对比
        data.forEach((item) => {
          (item.user_id !== creatorId) && (!editorIds?.includes(item.user_id)) && options.push({
            text: item.user_name ?? '',
            value: item.user_id,
          });
        });
        setStatus(options.length > 0 ? null : 'empty');
        setOptions(options);
      }
    }
  });
  const debounceGetUserList = useCallback(debounce(getUserList, 500), [getUserList]);

  return (<ConfigProvider locale={isEn ? "en" : "zh"}>
    <div className={'dbl-access-setting-modal-add-user-list'}>
      <SearchBox
        onChange={(searchStr) => {
          searchStrRef.current = searchStr;
          if (searchStr) {
            setStatus('loading');
            // setOptions((options) => {
            //   return options.length > 0 ? [] : options
            // })
            debounceGetUserList({ user_name: searchStr });
          } else {
            setStatus(null);
            setOptions([]);
            // setOptions(editors)
          }

        }}
        onClear={() => {
          if (status === 'loading') {
            cancelUserList();
            setStatus(null);
          }
          setOptions([]);
        }}
        placeholder={formatMessage('placeholder')}

      />
      {
        status ? (
          <StatusTip
            className='dbl-access-setting-modal-add-user-options-list padding-bt-20'
            status={status}
          // onRetry={() => fetch(keywordRef.current)}
          // emptyText="输入关键词搜索"
          />
        ) : (<List className='dbl-access-setting-modal-add-user-options-list' type={'option'}>
          {
            options?.map(opt => (<ListItem key={opt.value} onClick={() => {
              onSelectUser(opt);
              setOptions([]);
              // close();
            }}>{opt.text}</ListItem>))
          }
        </List>)
      }
    </div>
  </ConfigProvider>);
}


export default AccessSettingModal;